import { Injector, runInInjectionContext } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { catchError, EMPTY, expand, reduce, throwError } from 'rxjs';

import { RecursiveFetch } from '../types';

import { buildHttpParams } from './build-http-params.util';

export function validateErrorResponse(
  error: unknown,
): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse;
}

export function handleFetchError(error: unknown, url: string) {
  if (validateErrorResponse(error)) {
    console.error('HTTP Error:', {
      url: error.url,
      status: error.status,
      message: error.message,
      error: error.error,
    });
    return throwError(() => error);
  }
  return throwError(
    () =>
      new Error(
        `Error fetching data from ${url}: ${typeof error === 'object' && error && 'message' in error ? error.message : 'Unknown error'}`,
      ),
  );
}

export function recursiveFetch<T>(
  url: string,
  injector: Injector,
  params: Record<string, unknown>,
) {
  return runInInjectionContext(injector, () => {
    const _httpClient = injector.get(HttpClient);
    let _currentPage = 'page' in params ? +`${params['page']}` : 1;

    return _httpClient
      .get<RecursiveFetch<T>>(url, {
        params: buildHttpParams(params),
      })
      .pipe(
        catchError((error: unknown) => handleFetchError(error, url)),
        expand((response) => {
          _currentPage += 1;

          const _size =
            response.data && 'size' in response.data ? response.data.size : 0;
          const _total =
            response.data && 'total' in response.data
              ? response.data.total
              : 'total' in response
                ? +`${response.total}`
                : 0;
          const _totalPages =
            _total && _size > 0 ? Math.ceil(_total / _size) : _total;
          if (_currentPage <= _totalPages) {
            return _httpClient
              .get<RecursiveFetch<T>>(url, {
                params: buildHttpParams({ ...params, page: _currentPage }),
              })
              .pipe(
                catchError((error: unknown) => handleFetchError(error, url)),
              );
          }
          return EMPTY;
        }),
        reduce((acc, response) => {
          if (response && response.data) {
            acc['data'] = {
              ...response.data,
              results: [
                ...(acc.data?.results ?? []),
                ...(response.data?.results ?? []),
              ],
            };
          }
          return acc;
        }),
      );
  });
}
